import { useState, useCallback } from 'react';
import { aiService } from '../services/aiService';
import { vehicleService } from '../services/vehicleService';
import { MileageValidationResult } from '../types/vehicle';

interface UseMileageAIResult {
  analyzing: boolean;
  aiMileage: number | null;
  aiConfidence: number | null;
  validationResult: MileageValidationResult | null;
  error: string | null;
  analyzeImage: (file: File) => Promise<void>;
  validateMileage: (mileage: number, currentMileage: number, lastReportDate?: Date) => Promise<boolean>;
  reset: () => void;
}

export const useMileageAI = (): UseMileageAIResult => {
  const [analyzing, setAnalyzing] = useState(false);
  const [aiMileage, setAiMileage] = useState<number | null>(null);
  const [aiConfidence, setAiConfidence] = useState<number | null>(null);
  const [validationResult, setValidationResult] = useState<MileageValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setAnalyzing(false);
    setAiMileage(null);
    setAiConfidence(null);
    setValidationResult(null);
    setError(null);
  }, []);

  const analyzeImage = useCallback(async (file: File) => {
    try {
      setAnalyzing(true);
      setError(null);

      // Convert image to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          if (typeof reader.result === 'string') {
            // Remove data URL prefix
            const base64 = reader.result.split(',')[1];
            resolve(base64);
          } else {
            reject(new Error('Failed to read image'));
          }
        };
        reader.onerror = () => reject(reader.error);
      });

      reader.readAsDataURL(file);
      const base64 = await base64Promise;

      // Analyze image with OpenAI
      const extractedMileage = await aiService.extractMileageFromImage(base64);
      
      if (extractedMileage) {
        setAiMileage(extractedMileage);
        setAiConfidence(0.95); // Example confidence value
      } else {
        setError('Could not detect mileage from image');
      }
    } catch (err) {
      console.error('Error analyzing image:', err);
      setError(err instanceof Error ? err.message : 'Failed to analyze image');
    } finally {
      setAnalyzing(false);
    }
  }, []);

  const validateMileage = useCallback(async (
    mileage: number,
    currentMileage: number,
    lastReportDate?: Date
  ): Promise<boolean> => {
    try {
      setError(null);
      const result = await aiService.validateMileageReading(
        mileage,
        currentMileage,
        lastReportDate || new Date()
      );
      setValidationResult(result);
      return result.isValid;
    } catch (err) {
      console.error('Error validating mileage:', err);
      setError(err instanceof Error ? err.message : 'Failed to validate mileage');
      return false;
    }
  }, []);

  return {
    analyzing,
    aiMileage,
    aiConfidence,
    validationResult,
    error,
    analyzeImage,
    validateMileage,
    reset
  };
};
